import { ruteroPassengerFieldRef } from '@bandido/shared/types';
import type {
  RuteroDeclarablePassengerField,
  RuteroPassengerFieldRef,
} from '@bandido/shared/types';

import { missingPassengerFieldScopes } from './passengerFields';
import type { PassengerFieldLevel } from './passengerFields';

/**
 * Cómo se ROTULA lo que declara la oferta. Vive aparte de la resolución
 * (`passengerFields.ts`, sin i18n) y de los componentes: la lista que
 * acompaña al CTA bloqueado y la que dibuja la sección tienen que leerse
 * igual, y con dos formatos parecidos se leerían distinto.
 *
 * Solo se traduce el rótulo; el campo y el alcance son identificadores del
 * contrato y viajan tal cual (21.15).
 */
type Translate = (key: string) => string;

/**
 * El badge de cada nivel: qué decisión condiciona el dato, en el idioma del
 * asesor. Es un `Record` exhaustivo — un nivel nuevo no compila hasta que se
 * decide cómo se rotula y con qué peso visual.
 */
export const PASSENGER_FIELD_LEVEL_BADGE: Record<
  PassengerFieldLevel,
  { labelKey: string; tone: 'blocking' | 'confirm' | 'optional' }
> = {
  hold: {
    labelKey: 'travelServices.booking.fieldLevel.hold',
    tone: 'blocking',
  },
  quote: {
    labelKey: 'travelServices.booking.fieldLevel.quote',
    tone: 'blocking',
  },
  confirm: {
    labelKey: 'travelServices.booking.fieldLevel.confirm',
    tone: 'confirm',
  },
  recommended: {
    labelKey: 'travelServices.booking.fieldLevel.recommended',
    tone: 'optional',
  },
};

export function passengerFieldLabel(
  field: RuteroDeclarablePassengerField,
  t: Translate,
): string {
  return t(`travelServices.booking.paxFields.${field}`);
}

/**
 * Un ref del contrato (`contact.phone`) como lo lee el asesor: el campo y, entre
 * paréntesis, a quién se le pide. Los refs que no son del vocabulario ya los
 * descartó el saneo, así que acá no queda nada crudo del proveedor.
 *
 * Devuelve el ref JUNTO a su rótulo: una lista dibujada tiene que poder
 * identificar cada fila por el ref, y emparejar por índice contra la lista de
 * entrada se rompería en cuanto el saneo descarte uno.
 */
export function passengerFieldRefEntries(
  refs: readonly RuteroPassengerFieldRef[],
  t: Translate,
): { ref: RuteroPassengerFieldRef; label: string }[] {
  return missingPassengerFieldScopes(refs).map(({ scope, field }) => ({
    ref: ruteroPassengerFieldRef(scope, field),
    label: `${passengerFieldLabel(field, t)} (${t(
      `travelServices.booking.fieldScope.${scope}`,
    )})`,
  }));
}

export function passengerFieldRefLabels(
  refs: readonly RuteroPassengerFieldRef[],
  t: Translate,
): string[] {
  return passengerFieldRefEntries(refs, t).map((entry) => entry.label);
}
